<template>
  <div>
    <paper-modal-container :is-visible="true" title="결제 처리 중" :hide-close-button="true" @close="goHome">
      <div class="success-wrapper">
        <div v-if="state === 'processing'" class="state-block">
          <div class="spinner"></div>
          <p class="state-title">결제 확인 중입니다...</p>
          <p class="state-desc">잠시만 기다려 주세요. 이 화면을 닫지 마세요.</p>
        </div>

        <div v-else-if="state === 'success'" class="state-block">
          <div class="success-icon">✓</div>
          <p class="state-title state-success">예매가 완료되었습니다!</p>
          <p class="state-desc">결제가 정상적으로 처리되었습니다.</p>
          <div class="info-box">
            <div class="info-row">
              <span class="info-label">주문번호</span>
              <span class="info-value">{{ orderId }}</span>
            </div>
            <div v-if="amount" class="info-row">
              <span class="info-label">결제 금액</span>
              <span class="info-value">{{ formatWon(amount) }}</span>
            </div>
          </div>
          <button type="button" class="primary-btn" @click="goLiveDetail">공연 상세로 이동</button>
        </div>

        <div v-else-if="state === 'error'" class="state-block">
          <div class="error-icon">!</div>
          <p class="state-title state-error">결제 후 예매 처리 중 오류가 발생했습니다</p>
          <p class="state-desc">{{ errorMessage }}</p>
          <p v-if="orderId" class="state-desc-sub">주문번호: {{ orderId }}</p>
          <p class="state-desc-sub">결제는 완료되었을 수 있습니다. 관리자에게 문의해 주세요.</p>
          <button type="button" class="primary-btn" @click="goHome">홈으로 이동</button>
        </div>
      </div>
    </paper-modal-container>
  </div>
</template>

<script>
import PaperModalContainer from "@/components/modal/PaperModalContainer.vue";
import { createReservation } from "@/utils/write/ticket";
import { trackReservationComplete } from "@/utils/activityTracker";

export default {
  name: "TicketPaymentSuccess",
  components: { PaperModalContainer },
  data() {
    return {
      state: "processing",
      orderId: "",
      paymentKey: "",
      amount: 0,
      liveId: "",
      errorMessage: "",
    };
  },
  mounted() {
    this.processPayment();
  },
  methods: {
    formatWon(n) {
      const v = Number(n) || 0;
      return `${new Intl.NumberFormat("ko-KR").format(v)}원`;
    },
    goHome() {
      this.$router.push("/");
    },
    goLiveDetail() {
      if (this.liveId) {
        this.$router.push(`/live/${this.liveId}`);
      } else {
        this.goHome();
      }
    },
    async processPayment() {
      try {
        const query = this.$route.query;
        this.orderId = String(query.orderId || "");
        this.paymentKey = String(query.paymentKey || "");
        this.amount = Number(query.amount || 0);

        if (!this.orderId || !this.paymentKey || !this.amount) {
          throw new Error("결제 결과 정보가 없습니다.");
        }

        const key = `ticketPaymentPending:${this.orderId}`;
        const raw = sessionStorage.getItem(key);
        if (!raw) {
          throw new Error("결제 진행 정보가 만료되었습니다.");
        }
        const pending = JSON.parse(raw);
        this.liveId = pending.liveId || "";

        if (Number(pending.amount) !== this.amount) {
          throw new Error("결제 금액 정보가 일치하지 않습니다.");
        }

        // TODO: 백엔드 결제 승인 API 구현 후 아래 호출을 우선 처리해야 함
        //   await apiClient.post("/api/payments/toss/confirm", {
        //     paymentKey: this.paymentKey,
        //     orderId: this.orderId,
        //     amount: this.amount,
        //   });
        // 승인 응답 성공 시에만 createReservation 호출하도록 변경 필요.

        const reservationData = {
          ...pending.reservationData,
          paymentMethod: "card",
          paymentProvider: "tosspayments",
          paymentConfirmed: true,
          paymentKey: this.paymentKey,
          paymentOrderId: this.orderId,
          paymentAmount: this.amount,
          status: "confirmed",
        };

        await createReservation(reservationData);
        trackReservationComplete(this.liveId);

        sessionStorage.removeItem(key);
        sessionStorage.removeItem(`ticketPaymentDraft:${this.liveId}`);

        this.state = "success";
      } catch (err) {
        console.error("결제 후 처리 오류:", err);
        this.errorMessage = err?.message || "결제 처리 중 오류가 발생했습니다.";
        this.state = "error";
      }
    },
  },
};
</script>

<style scoped>
.success-wrapper {
  padding: 24px 0;
  max-width: 600px;
  margin: 0 auto;
}

.state-block {
  text-align: center;
  padding: 40px 20px;
}

.spinner {
  width: 48px;
  height: 48px;
  border: 4px solid #e2e8f0;
  border-top-color: #66bdff;
  border-radius: 50%;
  margin: 0 auto 20px;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.success-icon,
.error-icon {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 32px;
  font-family: "NanumSquareRoundEB", sans-serif;
  margin: 0 auto 20px;
  color: #fff;
}

.success-icon {
  background: linear-gradient(135deg, #48bb78 0%, #38a169 100%);
}

.error-icon {
  background: linear-gradient(135deg, #f56565 0%, #c53030 100%);
}

.state-title {
  font-family: "NanumSquareRoundEB", sans-serif;
  font-size: 18px;
  color: #2d3748;
  margin: 0 0 8px 0;
}

.state-title.state-success {
  color: #2f855a;
}

.state-title.state-error {
  color: #c53030;
}

.state-desc {
  font-family: "NanumSquareRoundR", sans-serif;
  font-size: 14px;
  color: #4a5568;
  margin: 0 0 8px 0;
  line-height: 1.6;
}

.state-desc-sub {
  font-family: "NanumSquareRoundR", sans-serif;
  font-size: 12px;
  color: #718096;
  margin: 0 0 4px 0;
}

.info-box {
  margin: 24px auto;
  max-width: 380px;
  text-align: left;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 16px 20px;
}

.info-row {
  display: flex;
  padding: 10px 0;
  border-bottom: 1px solid #f1f3f5;
}

.info-row:last-child {
  border-bottom: none;
}

.info-label {
  flex: 0 0 100px;
  font-family: "NanumSquareRoundB", sans-serif;
  font-size: 13px;
  color: #495057;
}

.info-value {
  flex: 1;
  font-family: "NanumSquareRoundR", sans-serif;
  font-size: 13px;
  color: #212529;
  overflow-wrap: anywhere;
}

.primary-btn {
  margin-top: 16px;
  padding: 12px 28px;
  background: #66bdff;
  color: #fff;
  border: none;
  border-radius: 8px;
  font-family: "NanumSquareRoundEB", sans-serif;
  font-size: 14px;
  cursor: pointer;
}

.primary-btn:hover {
  background: #4da3ff;
}
</style>
